import CircleIcon from '@mui/icons-material/Circle'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMore from '@mui/icons-material/ExpandMore'
import RefreshIcon from '@mui/icons-material/Refresh'
import SendIcon from '@mui/icons-material/Send'
import StorageRoundedIcon from '@mui/icons-material/StorageRounded'
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getSPDataWithParams,
  postSPDataWithoutParams,
} from 'src/supportPortal/services/services'
import BrowserIcon, { PwaBadge } from './BrowserIcon'

// Debug sections are deliberately a registry — more sections (network, feature flags,
// console traces...) slot in later without touching the drawer shell.
const DEBUG_SECTIONS = [
  { id: 'indexeddb', label: 'IndexedDB', icon: <StorageRoundedIcon fontSize="small" /> },
  { id: 'activity', label: 'Activity', icon: <TimelineRoundedIcon fontSize="small" /> },
]

const COMMAND_ACTIONS = [
  {
    type: 'IDB_REPORT',
    label: 'Fetch IndexedDB report',
    description: 'Store record counts, DB version, per-store sync timestamps, storage quota.',
    destructive: false,
  },
  {
    type: 'FORCE_FULL_RESYNC',
    label: 'Force full resync',
    description: 'Re-downloads every store from the server (keeps current data until replaced).',
    destructive: false,
  },
  {
    type: 'WIPE_AND_RESYNC',
    label: 'Wipe & resync',
    description: 'Deletes the local erm-database and clears sync timestamps, then resyncs from scratch.',
    destructive: true,
  },
  {
    type: 'CLEAR_SYNC_TIMESTAMPS',
    label: 'Clear sync timestamps',
    description: 'Clears the per-store last-sync markers so the next periodic run re-covers everything.',
    destructive: false,
  },
]

// while the drawer is open, "last active" tracks the clients' 90s check-in heartbeats
const DEVICES_REFRESH_MS = 60 * 1000
const COMMANDS_REFRESH_MS = 5 * 1000
const ACTIVITY_REFRESH_MS = 60 * 1000

const EVENT_META = {
  'log-check-in': { label: 'Checked in', color: '#16a34a' },
  're-check-in': { label: 'Re-checked in', color: '#16a34a' },
  'log-check-out': { label: 'Checked out', color: '#64748b' },
  'log-thrown-out': { label: 'Displaced by another check-in', color: '#dc2626' },
  'log-check-in-refresh': { label: 'Active', color: '#38bdf8' },
  'log-peek-in-refresh': { label: 'Peeked into room', color: '#a855f7' },
  'log-screen-idle': { label: 'Screen idle', color: '#f59e0b' },
  'enter-without-checkin': { label: 'Entered EMR without check-in', color: '#94a3b8' },
  'audit-checkin': { label: 'Audit check-in', color: '#6366f1' },
  'audit-checkout': { label: 'Audit check-out', color: '#6366f1' },
  'auto-connect-paused': { label: 'Auto-connect paused', color: '#f97316' },
  'auto-connect-resumed': { label: 'Auto-connect resumed', color: '#22c55e' },
  'log-check-in-request': { label: 'Check-in requested', color: '#0ea5e9' },
  'log-check-in-request-ack': { label: 'Check-in request accepted', color: '#0ea5e9' },
  'log-check-in-request-denied': { label: 'Check-in request denied', color: '#ef4444' },
}

function unwrap(raw, key) {
  return raw?.data?.data?.[key] || raw?.data?.[key] || raw?.[key]
}

function formatInstant(value) {
  if (!value) return '—'
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('DD MMM YYYY, HH:mm:ss') : String(value)
}

function formatTime(value) {
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('HH:mm:ss') : String(value)
}

function formatDuration(durationMs) {
  if (durationMs == null) return '—'
  const totalMinutes = Math.floor(durationMs / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  if (totalMinutes > 0) return `${totalMinutes}m`
  return `${Math.max(1, Math.round(durationMs / 1000))}s`
}

function statusChipColor(status) {
  switch (status) {
    case 'RESPONDED':
      return 'success'
    case 'PENDING':
      return 'warning'
    case 'CANCELLED':
      return 'default'
    case 'EXPIRED':
      return 'error'
    default:
      return 'default'
  }
}

function roomDisplay(session) {
  return (
    session.chamberRoomTitle ||
    session.chamberRoomId ||
    null
  )
}

/** The "clean reader" for one device's check-in history: sessions with durations. */
function DeviceSessionsTable({ sessions }) {
  if (!sessions?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 1, pb: 1 }}>
        No check-in sessions in this window.
      </Typography>
    )
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Checked in</TableCell>
          <TableCell>Checked out</TableCell>
          <TableCell>Duration</TableCell>
          <TableCell>Method</TableCell>
          <TableCell>Room</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sessions.map((session, idx) => (
          <TableRow key={idx}>
            <TableCell>{formatInstant(session.checkinAt)}</TableCell>
            <TableCell>
              {session.open ? (
                <Chip label="still open" size="small" color="success" variant="outlined" />
              ) : (
                formatInstant(session.checkoutAt)
              )}
            </TableCell>
            <TableCell>{session.open ? '—' : formatDuration(session.durationMs)}</TableCell>
            <TableCell>
              {(session.checkinMethod || '—').replace('check-in-method-', '')}
            </TableCell>
            <TableCell>
              {session.chamberRoomTitle ? (
                <Tooltip title={session.chamberRoomId || ''}>
                  <span>{session.chamberRoomTitle}</span>
                </Tooltip>
              ) : (
                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {roomDisplay(session) || '—'}
                </span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/** Renders an IDB_REPORT result payload as a readable table, others as JSON. */
function ResultReportView({ commandType, report }) {
  if (!report) return null
  if (commandType === 'IDB_REPORT' && report.storeCounts) {
    return (
      <Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} sx={{ mb: 1 }}>
          <Chip size="small" label={`DB v${report.dbVersion}`} />
          {report.storage?.usageBytes != null && (
            <Chip
              size="small"
              label={`Storage ${(report.storage.usageBytes / (1024 * 1024)).toFixed(1)} MB used`}
            />
          )}
          {report.checkedIn?.clinicBrandId && (
            <Chip size="small" color="success" label={`Checked in: ${report.checkedIn.clinicBrandId}`} />
          )}
          {report.onLine === false && <Chip size="small" color="error" label="Client offline" />}
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Store</TableCell>
              <TableCell align="right">Records</TableCell>
              <TableCell>Last synced (server data)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(report.storeCounts).map(([store, count]) => (
              <TableRow key={store}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{store}</TableCell>
                <TableCell align="right">{String(count)}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  {report.lastSyncedAt?.[
                    Object.keys(report.lastSyncedAt || {}).find(
                      (k) => k.toLowerCase().replace(/_/g, '') ===
                        store.toLowerCase().replace(/_/g, ''),
                    )
                  ] || ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    )
  }
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        bgcolor: 'grey.100',
        borderRadius: 1,
        fontSize: 12,
        maxHeight: 260,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {JSON.stringify(report, null, 2)}
    </Box>
  )
}

/**
 * Collapse consecutive heartbeat refreshes (same device + room) into one "Active" span so
 * the timeline reads as activity, not as one dot per 90-second heartbeat.
 */
function collapseTimelineEvents(events) {
  const collapsed = []
  for (const event of events) {
    const previous = collapsed[collapsed.length - 1]
    const isRefresh = event.logEventId === 'log-check-in-refresh'
    if (
      isRefresh &&
      previous?.logEventId === 'log-check-in-refresh' &&
      previous.deviceKey === event.deviceKey &&
      previous.chamberRoomId === event.chamberRoomId
    ) {
      // events arrive newest-first: extend the span backwards
      previous.spanFromAt = event.at
      previous.heartbeats += 1
      continue
    }
    collapsed.push({ ...event, heartbeats: isRefresh ? 1 : 0, spanFromAt: event.at })
  }
  return collapsed
}

function ActivityTimeline({ practitionerId, devices }) {
  const [windowDays, setWindowDays] = useState(7)
  const [deviceFilter, setDeviceFilter] = useState('')
  const [payload, setPayload] = useState(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(
    async (silent = false) => {
      if (!practitionerId) return
      if (!silent) setLoading(true)
      try {
        const resp = await getSPDataWithParams('/api/secure/cc/support/emr-debug/activity', {
          practitionerId,
          days: windowDays,
          limit: 600,
        })
        setPayload(unwrap(resp, 'emrDebugActivity') || null)
      } catch (error) {
        if (!silent) {
          toast.error(error?.response?.data?.description || 'Could not load activity')
        }
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [practitionerId, windowDays],
  )

  useEffect(() => {
    load()
    const interval = setInterval(() => load(true), ACTIVITY_REFRESH_MS)
    return () => clearInterval(interval)
  }, [load])

  const timeline = useMemo(() => {
    const events = (payload?.events || []).filter(
      (event) => !deviceFilter || event.deviceKey === deviceFilter,
    )
    const collapsed = collapseTimelineEvents(events)
    const byDay = new Map()
    for (const event of collapsed) {
      const day = dayjs(event.at).format('ddd, DD MMM YYYY')
      if (!byDay.has(day)) byDay.set(day, [])
      byDay.get(day).push(event)
    }
    return [...byDay.entries()]
  }, [payload, deviceFilter])

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" rowGap={1} sx={{ mb: 1.5 }}>
        <TextField
          select
          size="small"
          label="Window"
          value={windowDays}
          onChange={(e) => setWindowDays(Number(e.target.value))}
          sx={{ width: 120 }}
        >
          {[1, 7, 14, 30].map((d) => (
            <MenuItem key={d} value={d}>
              {d} day{d === 1 ? '' : 's'}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Device"
          value={deviceFilter}
          onChange={(e) => setDeviceFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All devices</MenuItem>
          {devices.map((device) => (
            <MenuItem key={device.deviceKey} value={device.deviceKey}>
              {device.label}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="Reload activity">
          <span>
            <IconButton size="small" onClick={() => load()} disabled={loading}>
              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {payload?.truncated && (
          <Chip size="small" color="warning" label="Truncated — narrow the window" />
        )}
      </Stack>

      {timeline.length === 0 && !loading && (
        <Alert severity="info">No EMR activity in this window.</Alert>
      )}

      {timeline.map(([day, events]) => (
        <Box key={day} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {day}
          </Typography>
          <Box sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 2, ml: 1 }}>
            {events.map((event, idx) => {
              const meta = EVENT_META[event.logEventId] || {
                label: event.logEventId,
                color: '#94a3b8',
              }
              const isSpan = event.heartbeats > 1
              return (
                <Stack
                  key={idx}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  sx={{ position: 'relative', py: 0.5 }}
                >
                  <CircleIcon
                    sx={{
                      fontSize: 10,
                      color: meta.color,
                      position: 'absolute',
                      left: -23,
                      top: 10,
                    }}
                  />
                  <Typography variant="caption" sx={{ minWidth: 118, pt: 0.25, color: 'text.secondary', fontFamily: 'monospace' }}>
                    {isSpan
                      ? `${formatTime(event.spanFromAt)}–${formatTime(event.at)}`
                      : formatTime(event.at)}
                  </Typography>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: meta.color }}>
                        {meta.label}
                        {isSpan ? ` (${event.heartbeats} heartbeats)` : ''}
                      </Typography>
                      {(event.chamberRoomTitle || event.chamberRoomId) && (
                        <Tooltip title={event.chamberRoomId || ''}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={event.chamberRoomTitle || event.chamberRoomId}
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        </Tooltip>
                      )}
                      <BrowserIcon browserName={event.browserName} size={15} />
                      <PwaBadge pwa={event.pwa} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {[
                        event.osName,
                        event.deviceType,
                        (event.checkinMethod || '').replace('check-in-method-', ''),
                        event.clinicBrandId,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </Box>
                </Stack>
              )
            })}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default function PractitionerDebugDrawer({ practitioner, open, onClose }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const practitionerId = practitioner?.id || practitioner?.userId
  const [activeSection, setActiveSection] = useState(DEBUG_SECTIONS[0].id)

  const [devicesPayload, setDevicesPayload] = useState(null)
  const [devicesLoading, setDevicesLoading] = useState(false)
  const [commands, setCommands] = useState([])
  const [commandsLoading, setCommandsLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [realTime, setRealTime] = useState(true)
  const [targetDeviceId, setTargetDeviceId] = useState('')
  const pollRef = useRef(null)

  const loadDevices = useCallback(
    async (silent = false) => {
      if (!practitionerId) return
      if (!silent) setDevicesLoading(true)
      try {
        const resp = await getSPDataWithParams('/api/secure/cc/support/emr-debug/devices', {
          practitionerId,
        })
        setDevicesPayload(unwrap(resp, 'emrDebugDevices') || null)
      } catch (error) {
        if (!silent) {
          toast.error(error?.response?.data?.description || 'Could not load devices')
        }
      } finally {
        if (!silent) setDevicesLoading(false)
      }
    },
    [practitionerId],
  )

  const loadCommands = useCallback(
    async (silent = false) => {
      if (!practitionerId) return
      if (!silent) setCommandsLoading(true)
      try {
        const resp = await getSPDataWithParams('/api/secure/cc/support/emr-debug/commands', {
          practitionerId,
          limit: 25,
        })
        setCommands(unwrap(resp, 'emrDebugCommands') || [])
      } catch (error) {
        if (!silent) {
          toast.error(error?.response?.data?.description || 'Could not load commands')
        }
      } finally {
        if (!silent) setCommandsLoading(false)
      }
    },
    [practitionerId],
  )

  // while open: commands poll fast (real-time results), devices poll each minute so
  // "last active" follows the clients' 90-second check-in heartbeats
  useEffect(() => {
    if (!open) return undefined
    loadDevices()
    loadCommands()
    pollRef.current = setInterval(() => loadCommands(true), COMMANDS_REFRESH_MS)
    const devicesInterval = setInterval(() => loadDevices(true), DEVICES_REFRESH_MS)
    return () => {
      clearInterval(pollRef.current)
      pollRef.current = null
      clearInterval(devicesInterval)
    }
  }, [open, loadDevices, loadCommands])

  const devices = useMemo(() => devicesPayload?.devices || [], [devicesPayload])

  const runCommand = async (action) => {
    if (!practitionerId || creating) return
    if (
      action.destructive &&
      !window.confirm(
        `${action.label} on ${practitioner?.name || 'this practitioner'}'s client(s)? ${action.description}`,
      )
    ) {
      return
    }
    setCreating(true)
    try {
      const resp = await postSPDataWithoutParams(
        '/api/secure/cc/support/emr-debug/commands',
        {
          practitionerId,
          commandType: action.type,
          targetDeviceId: targetDeviceId || undefined,
          realTime,
        },
      )
      const command = unwrap(resp, 'emrDebugCommand')
      const signalNote =
        realTime && command?.lastSignalResult === 'sent'
          ? ' — real-time signal delivered'
          : realTime
          ? ` — signal: ${command?.lastSignalResult || 'not sent'} (client will pick it up on its next poll)`
          : ' — queued; the client picks it up on its next poll'
      toast.success(`${action.label} queued${signalNote}`)
      loadCommands(true)
    } catch (error) {
      toast.error(error?.response?.data?.description || `Could not queue ${action.label}`)
    } finally {
      setCreating(false)
    }
  }

  const resignal = async (commandId) => {
    try {
      await postSPDataWithoutParams(
        `/api/secure/cc/support/emr-debug/commands/${commandId}/resignal`,
        {},
      )
      toast.success('Real-time signal re-sent')
      loadCommands(true)
    } catch (error) {
      toast.error(error?.response?.data?.description || 'Could not re-signal')
    }
  }

  const cancel = async (commandId) => {
    try {
      await postSPDataWithoutParams(
        `/api/secure/cc/support/emr-debug/commands/${commandId}/cancel`,
        {},
      )
      loadCommands(true)
    } catch (error) {
      toast.error(error?.response?.data?.description || 'Could not cancel')
    }
  }

  const deviceOptions = useMemo(
    () => devices.filter((device) => device.deviceId),
    [devices],
  )

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 860,
          maxWidth: '100vw',
          height: isMobile ? '92vh' : '100%',
          borderTopLeftRadius: isMobile ? 16 : 0,
          borderTopRightRadius: isMobile ? 16 : 0,
        },
      }}
    >
      {/* header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Debug — {practitioner?.name || practitioner?.fullName || practitionerId}
        </Typography>
        <Chip size="small" label={practitionerId} sx={{ fontFamily: 'monospace' }} />
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* section tabs — future debug sections mount here */}
      <Tabs
        value={activeSection}
        onChange={(e, value) => setActiveSection(value)}
        sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 1.5, minHeight: 42 }}
      >
        {DEBUG_SECTIONS.map((section) => (
          <Tab
            key={section.id}
            value={section.id}
            icon={section.icon}
            iconPosition="start"
            label={section.label}
            sx={{ minHeight: 42, textTransform: 'none', fontWeight: 600 }}
          />
        ))}
      </Tabs>

      <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1 }}>
        {activeSection === 'indexeddb' && (
          <Stack spacing={2.5}>
            {/* ---- devices from EMR check-in logs */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Devices (from EMR check-in logs
                  {devicesPayload?.windowDays ? `, last ${devicesPayload.windowDays} days` : ''})
                </Typography>
                <Tooltip title="Reload devices (auto-refreshes every minute while open)">
                  <span>
                    <IconButton size="small" onClick={() => loadDevices()} disabled={devicesLoading}>
                      {devicesLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              {devices.length === 0 && !devicesLoading && (
                <Alert severity="info">
                  No check-ins in the window — the practitioner hasn&apos;t used the EMR
                  recently (or their app predates device ids; older check-ins group by
                  browser/OS signature).
                </Alert>
              )}
              {devices.map((device) => (
                <Accordion key={device.deviceKey} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                      <Tooltip
                        title={
                          device.likelyOnline
                            ? 'Heartbeat seen within the last 5 minutes'
                            : 'No recent heartbeat'
                        }
                      >
                        <CircleIcon
                          sx={{ fontSize: 12 }}
                          color={device.likelyOnline ? 'success' : 'disabled'}
                        />
                      </Tooltip>
                      <BrowserIcon browserName={device.clientDeviceInfo?.browserName} size={18} />
                      <Typography sx={{ fontWeight: 600 }}>{device.label}</Typography>
                      <PwaBadge pwa={device.clientDeviceInfo?.pwa} />
                      {device.deviceId && (
                        <Chip size="small" sx={{ fontFamily: 'monospace' }}
                          label={device.deviceId.slice(0, 16)} />
                      )}
                      <Typography variant="body2" color="text.secondary">
                        last active {formatInstant(device.lastSeenAt)} ·{' '}
                        {device.checkinCount} check-in{device.checkinCount === 1 ? '' : 's'}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {device.clientDeviceInfo?.osName} {device.clientDeviceInfo?.osVersion} ·{' '}
                      {device.clientDeviceInfo?.browserName} {device.clientDeviceInfo?.browserVersion} ·{' '}
                      {device.clientDeviceInfo?.screenWidth}×{device.clientDeviceInfo?.screenHeight} · app{' '}
                      {device.clientDeviceInfo?.appVersion || '?'} · IP{' '}
                      {device.clientDeviceInfo?.ipAddress || '?'}
                    </Typography>
                    <DeviceSessionsTable sessions={device.sessions} />
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider />

            {/* ---- actions */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Actions
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" rowGap={1} sx={{ mb: 1.5 }}>
                <FormControlLabel
                  control={<Switch checked={realTime} onChange={(e) => setRealTime(e.target.checked)} />}
                  label="Real-time (Agora signal to the connected client)"
                />
                <TextField
                  select
                  size="small"
                  label="Target device"
                  value={targetDeviceId}
                  onChange={(e) => setTargetDeviceId(e.target.value)}
                  sx={{ minWidth: 240 }}
                  helperText="All devices respond when unset"
                >
                  <MenuItem value="">All devices</MenuItem>
                  {deviceOptions.map((device) => (
                    <MenuItem key={device.deviceId} value={device.deviceId}>
                      {device.label} ({device.deviceId.slice(0, 12)}…)
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" rowGap={1.5}>
                {COMMAND_ACTIONS.map((action) => (
                  <Tooltip key={action.type} title={action.description}>
                    <span>
                      <Button
                        variant={action.type === 'IDB_REPORT' ? 'contained' : 'outlined'}
                        color={action.destructive ? 'warning' : 'primary'}
                        size="small"
                        startIcon={<SendIcon sx={{ fontSize: 14 }} />}
                        disabled={creating}
                        onClick={() => runCommand(action)}
                      >
                        {action.label}
                      </Button>
                    </span>
                  </Tooltip>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Real-time reaches the currently connected client instantly; every other
                signed-in device picks queued commands up on its next app load / focus /
                30-minute sync tick (commands expire after 24h by default).
              </Typography>
            </Box>

            <Divider />

            {/* ---- command history + results */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Commands & results
                </Typography>
                <Tooltip title="Reload commands">
                  <span>
                    <IconButton size="small" onClick={() => loadCommands()} disabled={commandsLoading}>
                      {commandsLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              {commands.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No debug commands yet for this practitioner.
                </Typography>
              )}
              {commands.map((command) => (
                <Accordion key={command.id} disableGutters defaultExpanded={command.status === 'RESPONDED'}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                      <Chip size="small" label={command.commandType} sx={{ fontWeight: 700 }} />
                      <Chip size="small" color={statusChipColor(command.status)} label={command.status} />
                      {command.targetDeviceId && (
                        <Chip size="small" variant="outlined" sx={{ fontFamily: 'monospace' }}
                          label={`→ ${command.targetDeviceId.slice(0, 12)}…`} />
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {formatInstant(command.createdTime)}
                        {command.realTimeRequested
                          ? ` · signal ${command.lastSignalResult || 'pending'}`
                          : ' · scheduled'}
                        {` · ${command.results?.length || 0} device result(s)`}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    {command.status === 'PENDING' && (
                      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                        <Button size="small" variant="outlined" onClick={() => resignal(command.id)}>
                          Re-send real-time signal
                        </Button>
                        <Button size="small" color="warning" onClick={() => cancel(command.id)}>
                          Cancel
                        </Button>
                      </Stack>
                    )}
                    {(command.results || []).map((result, idx) => (
                      <Box key={idx} sx={{ mb: 1.5 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                          <Chip
                            size="small"
                            color={result.status === 'ok' ? 'success' : 'error'}
                            label={result.status}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {result.deviceLabel || result.deviceId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            via {result.deliveredVia} · {formatInstant(result.completedAt)} · app{' '}
                            {result.appVersion || '?'}
                          </Typography>
                        </Stack>
                        {result.error && <Alert severity="error">{result.error}</Alert>}
                        <ResultReportView commandType={command.commandType} report={result.report} />
                      </Box>
                    ))}
                    {(command.results || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No device has responded yet
                        {command.status === 'PENDING'
                          ? ' — waiting for a client to pick this up.'
                          : '.'}
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Stack>
        )}

        {activeSection === 'activity' && (
          <ActivityTimeline practitionerId={practitionerId} devices={devices} />
        )}
      </Box>
    </Drawer>
  )
}
